// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { format, parseISO } from "date-fns";
import { Check, X } from "lucide-react";
import { useState } from "react";
import type { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDate } from "@/lib/format";
import { useDateFormat } from "@/lib/preferences";
import { cn } from "@/lib/utils";

const PILL_STYLE =
  "bg-green-100 text-green-900 border border-green-500/60 dark:bg-green-950 dark:text-green-300 dark:border-green-700";

export interface FilterDateRangeOption {
  /**
   * Stable id — this is what lands in the URL and in saved presets, so never renumber it. Only the
   * id is stored, never a span resolved from it: a rolling option ("next 30 days") saved as literal
   * dates would freeze its window at the day the preset was saved. The caller turns the id into
   * query params at fetch time.
   */
  value: string;
  label: string;
}

export interface DateRangeFilterValue {
  /** A chosen option's id. Mutually exclusive with `from`/`to`. */
  option?: string;
  /** Inclusive `yyyy-MM-dd` bounds of a hand-picked span. A single day is `from === to`. */
  from?: string;
  to?: string;
}

interface FilterDateRangeProps {
  label: string;
  value: DateRangeFilterValue;
  onChange: (value: DateRangeFilterValue) => void;
  options: FilterDateRangeOption[];
  /**
   * Last month the calendar reaches, in full. Without it the year dropdown stops at the end of the
   * current year (react-day-picker's default), so pass one wherever future dates are meaningful.
   */
  toMonth?: Date;
}

/** `yyyy-MM-dd` is the emitted *value*, not a display format — never localise it. */
function toIsoDay(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/**
 * One filter-bar widget covering both halves of a date question: a short list of named options, and
 * a calendar for picking a single day or a span of them. The two are mutually exclusive — choosing
 * either clears the other — so the trigger only ever has one thing to show.
 */
export function FilterDateRange({ label, value, onChange, options, toMonth }: FilterDateRangeProps) {
  const { t } = useTranslation("back-office");
  const [dateFormat] = useDateFormat();
  const [open, setOpen] = useState(false);

  const selectedOption = options.find((o) => o.value === value.option);
  const from = value.from ? parseISO(value.from) : undefined;
  const to = value.to ? parseISO(value.to) : undefined;
  const isSet = !!(selectedOption || from || to);

  function pillLabel(): string {
    if (selectedOption) return selectedOption.label;
    if (from && to)
      return value.from === value.to
        ? formatDate(from, dateFormat)
        : `${formatDate(from, dateFormat)} → ${formatDate(to, dateFormat)}`;
    if (from) return `${formatDate(from, dateFormat)} →`;
    if (to) return `→ ${formatDate(to, dateFormat)}`;
    return "";
  }

  function clear() {
    onChange({});
    // Closes the popover too: `Clear` is a finishing action, and leaving it open under the cursor
    // reads as if the click missed.
    setOpen(false);
  }

  function selectOption(option: FilterDateRangeOption) {
    // Re-clicking the active option clears the filter, matching the pill's own X.
    onChange(option.value === value.option ? {} : { option: option.value });
    setOpen(false);
  }

  function selectRange(range: DateRange | undefined) {
    if (!range?.from) {
      clear();
      return;
    }
    // react-day-picker leaves `to` undefined on the first click of a span; treating that as a
    // single day keeps the filter usable without a second click.
    onChange({ from: toIsoDay(range.from), to: toIsoDay(range.to ?? range.from) });
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "font-normal gap-1.5",
            isSet
              ? "bg-sidebar-accent-hover hover:bg-sidebar-accent border-sidebar-accent hover:border-sidebar-primary text-sidebar-accent-foreground hover:text-sidebar-accent-foreground dark:bg-sidebar-accent dark:border-sidebar-accent dark:hover:bg-sidebar-accent-hover dark:hover:border-sidebar-primary"
              : "border-dashed",
          )}
        >
          <span>{label}</span>
          {isSet && (
            <>
              <span
                className={cn(
                  "inline-flex items-center rounded-md px-2 py-0.5 text-sm font-medium tabular-nums",
                  PILL_STYLE,
                )}
              >
                {pillLabel()}
              </span>
              {/* Rendered as a span, not a <button>: the PopoverTrigger renders this Button as a
                  <button>, and a nested <button> is invalid HTML. */}
              <span
                role="button"
                tabIndex={0}
                aria-label={t("common.clear")}
                className="ml-0.5 text-muted-foreground hover:text-foreground transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  clear();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    clear();
                  }
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <X className="h-3 w-3" />
              </span>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" side="bottom" sideOffset={-36}>
        {/* The option rows borrow the action-menu recipe — `p-1` on the list, `rounded-sm px-2
            py-1.5` on each row — so the highlight sits inset with a gutter either side instead of
            running edge to edge. The virtualized filter lists are full-bleed on purpose; a short
            named-option list reads as a menu. */}
        <div className="p-1">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cn(
                "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:bg-accent focus-visible:outline-hidden",
                option.value === value.option && "bg-accent/60",
              )}
              onClick={() => selectOption(option)}
            >
              <span className="flex-1 truncate">{option.label}</span>
              {option.value === value.option && <Check className="h-4 w-4 shrink-0 text-primary" />}
            </button>
          ))}
        </div>
        <div className="border-t">
          <Calendar
            mode="range"
            captionLayout="dropdown"
            selected={from || to ? { from, to } : undefined}
            onSelect={selectRange}
            defaultMonth={from ?? to}
            endMonth={toMonth}
          />
        </div>
        {isSet && (
          <div className="flex justify-end border-t p-1">
            <Button variant="ghost" size="sm" onClick={clear}>
              {t("common.clear")}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
